/**
    * @description      : 
    * @author           : 
    * @group            : 
    * @created          : 31/08/2025 - 13:39:58
    * 
    * MODIFICATION LOG
    * - Version         : 1.0.0
    * - Date            : 31/08/2025
    * - Author          : 
    * - Modification    : 
**/
import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardMedia,
  Chip,
  Container,
  Divider,
  Grid,
  Paper,
  Typography,
  useTheme,
  alpha,
  styled
} from '@mui/material';
import {
  Code as CodeIcon,
  CloudQueue as CloudQueueIcon,
  Sync as SyncIcon,
  Storage as StorageIcon,
  ViewModule as ViewModuleIcon,
  Assessment as AssessmentIcon,
  Settings as SettingsIcon,
  Build as BuildIcon,
  Animation as AnimationIcon,
  Architecture as ArchitectureIcon,
  Monitor as MonitorIcon,
  CheckCircleOutline as CheckCircleIcon,
  Autorenew as AutorenewIcon,
  SettingsApplications as SettingsApplicationsIcon
} from '@mui/icons-material';
import Footer from '../../components/footer';
import Navbar from '../../components/Navbar';
import LetsTalk from '../../components/letsTalk';

// Define types for our state and data structures
interface VisibilityState {
  hero: boolean;
  overview: boolean;
  features: boolean;
  process: boolean;
  technologies: boolean;
  cta: boolean;
  testimonials: boolean;
}

interface SectionRefs {
  hero: React.RefObject<HTMLElement>;
  overview: React.RefObject<HTMLElement>;
  features: React.RefObject<HTMLElement>;
  process: React.RefObject<HTMLElement>;
  technologies: React.RefObject<HTMLElement>;
  cta: React.RefObject<HTMLElement>;
  testimonials: React.RefObject<HTMLElement>;
}

interface PortfolioProject {
  id: number;
  title: string;
  description: string;
  image: string;
  tags: string[];
}

interface FeatureItem {
  icon: React.ReactNode;
  title: string;
  description: string;
  delay: number;
}

interface ProcessStep {
  icon: React.ReactNode;
  title: string;
  description: string;
  delay: number;
}

interface Technology {
  name: string;
  icon: React.ReactNode;
  delay: number;
}

// Custom styled components
const HeroBox = styled(Box)(({ theme }) => ({
  position: 'relative',
  overflow: 'hidden',
  backgroundColor: '#044D76', // Updated to the requested color
  color: theme.palette.common.white,
  padding: theme.spacing(10, 0),
  [theme.breakpoints.up('md')]: {
    padding: theme.spacing(16, 0),
  },
}));

const DevOpsPipelineBg = styled(Box)(({ theme }) => ({
  position: 'absolute',
  inset: 0,
  opacity: 0.05,
  overflow: 'hidden',
}));

const GradientCircle = styled(Box)(({ theme }) => ({
  position: 'absolute',
  borderRadius: '50%',
}));

const FadeInBox = styled(Box)<{ visible: boolean; delay?: number }>(({ theme, visible, delay = 0 }) => ({
  transition: 'all 1s ease',
  opacity: visible ? 1 : 0,
  transform: visible ? 'translateY(0)' : 'translateY(10px)',
  transitionDelay: `${delay}ms`,
}));

const FeatureCard = styled(Paper)<{ visible: boolean; delay?: number }>(({ theme, visible, delay = 0 }) => ({
  padding: theme.spacing(3),
  height: '100%',
  transition: 'all 0.5s ease',
  opacity: visible ? 1 : 0,
  transform: visible ? 'translateY(0)' : 'translateY(10px)',
  transitionDelay: `${delay}ms`,
  backgroundColor: '#ffffff',
  '&:hover': {
    transform: 'translateY(-5px)',
    boxShadow: theme.shadows[10],
    '& .featureIcon': {
      transform: 'scale(1.1)',
    },
    '& .featureTitle': {
      color: '#044D76', // Updated to match button color
    },
  },
}));

const IconBox = styled(Box)(({ theme }) => ({
  transition: 'transform 0.3s ease',
  marginBottom: theme.spacing(2),
}));

const TechCard = styled(Paper)<{ visible: boolean; delay?: number }>(({ theme, visible, delay = 0 }) => ({
  padding: theme.spacing(3),
  backgroundColor: theme.palette.common.white,
  borderRadius: '12px',
  textAlign: 'center',
  transition: 'all 0.5s ease',
  opacity: visible ? 1 : 0,
  transform: visible ? 'translateY(0)' : 'translateY(10px)',
  transitionDelay: `${delay}ms`,
  boxShadow: '0 6px 20px rgba(0, 0, 0, 0.05)',
  border: '1px solid rgba(0, 153, 255, 0.1)',
  '&:hover': {
    transform: 'translateY(-5px)',
    boxShadow: '0 12px 28px rgba(0, 0, 0, 0.12)',
    '& .techIcon': {
      transform: 'scale(1.15)',
      backgroundColor: 'rgba(4, 77, 118, 0.15)',
    }
  },
}));

const ProcessTimelineLine = styled(Box)(({ theme }) => ({
  position: 'absolute',
  left: '50%',
  transform: 'translateX(-50%)',
  height: '100%',
  width: '2px',
  backgroundColor: 'rgba(4, 77, 118, 0.2)', // Updated to match theme
  opacity: 0.5,
}));

const ProcessStepBox = styled(Box)<{ visible: boolean; delay?: number }>(({ theme, visible, delay = 0 }) => ({
  display: 'flex',
  alignItems: 'center',
  marginBottom: theme.spacing(6),
  transition: 'all 1s ease',
  opacity: visible ? 1 : 0,
  transform: visible ? 'translateY(0)' : 'translateY(10px)',
  transitionDelay: `${delay}ms`,
}));

const ProcessIconBox = styled(Box)(({ theme }) => ({
  zIndex: 10,
  flexShrink: 0,
  width: 64,
  height: 64,
  borderRadius: '50%',
  backgroundColor: '#044D76', // Updated to match button color
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: theme.palette.common.white,
  boxShadow: theme.shadows[5],
}));

const ProjectCard = styled(Card)(({ theme }) => ({
  position: 'relative',
  overflow: 'hidden',
  height: '100%',
  borderRadius: '12px',
  transition: 'transform 0.3s ease, box-shadow 0.3s ease',
  '&:hover': {
    boxShadow: theme.shadows[10],
    '& .projectImage': {
      transform: 'scale(1.1)',
    },
    '& .projectOverlay': {
      opacity: 1,
    },
  },
}));

const ProjectImage = styled('img')({
  height: '100%',
  width: '100%',
  objectFit: 'cover',
  transition: 'transform 0.5s ease',
});

const ProjectOverlay = styled(Box)(({ theme }) => ({
  position: 'absolute',
  inset: 0,
  background: `linear-gradient(to top, ${theme.palette.common.black}cc, ${theme.palette.common.black}66, transparent)`,
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'flex-end',
  padding: theme.spacing(3),
  opacity: 0,
  transition: 'opacity 0.3s ease',
}));

const TechIconBox = styled(Box)(({ theme }) => ({
  height: 64,
  width: 64,
  borderRadius: '50%',
  backgroundColor: 'rgba(4, 77, 118, 0.1)', // Updated color
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  margin: '0 auto 16px auto',
  transition: 'all 0.3s ease',
}));

const CodeEditor = styled(Box)(({ theme }) => ({
  backgroundColor: '#282c34',
  borderRadius: '10px',
  overflow: 'hidden',
  boxShadow: '0 10px 30px rgba(0, 0, 0, 0.1)',
  transition: 'all 0.3s ease',
  '&:hover': {
    boxShadow: '0 15px 40px rgba(0, 0, 0, 0.2)',
    transform: 'translateY(-5px)',
  },
}));

const EditorHeader = styled(Box)(({ theme }) => ({
  backgroundColor: '#21252b',
  padding: '10px 16px',
  display: 'flex',
  alignItems: 'center',
  borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
}));

const EditorDot = styled(Box)<{ color: string }>(({ color }) => ({
  width: 12,
  height: 12,
  borderRadius: '50%',
  backgroundColor: color,
  marginRight: 8,
}));

const EditorFilename = styled(Typography)(({ theme }) => ({
  color: 'rgba(255, 255, 255, 0.7)',
  fontSize: '0.85rem',
  fontFamily: 'monospace',
  marginLeft: 8,
}));

const CodeContent = styled(Box)(({ theme }) => ({
  padding: '16px',
  overflowX: 'auto',
  maxHeight: '500px',
  overflowY: 'auto',
  '&::-webkit-scrollbar': {
    width: '8px',
    height: '8px',
  },
  '&::-webkit-scrollbar-thumb': {
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
    borderRadius: '4px',
  },
  '&::-webkit-scrollbar-track': {
    backgroundColor: 'rgba(255, 255, 255, 0.05)',
  },
}));

const PreformattedCode = styled('pre')(({ theme }) => ({
  margin: 0,
  fontFamily: 'JetBrains Mono, monospace',
  fontSize: '0.85rem',
  lineHeight: 1.5,
  color: '#e6e6e6',
}));

const TestimonialCard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(5),
  borderRadius: theme.shape.borderRadius * 3,
  backgroundColor: theme.palette.common.white,
  position: 'relative',
  boxShadow: '0 10px 40px rgba(0, 0, 0, 0.05)',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 30,
    left: 30,
    width: 60,
    height: 60,
    borderRadius: '50% 0 50% 50%',
    backgroundColor: 'rgba(4, 77, 118, 0.1)',
    zIndex: 0
  }
}));

const TestimonialNavButton = styled(Box)(({ theme }) => ({
  position: 'absolute',
  width: 48,
  height: 48,
  borderRadius: '50%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  color: '#044D76',
  border: '2px solid rgba(4, 77, 118, 0.2)',
  zIndex: 2,
  '&:hover': {
    backgroundColor: 'rgba(4, 77, 118, 0.1)'
  }
}));

const CodeBlockBox = styled(Box)(({ theme }) => ({
  position: 'absolute',
  inset: 0,
  opacity: 0.05,
  overflow: 'hidden',
  fontFamily: 'monospace',
  fontSize: '8px',
  [theme.breakpoints.up('md')]: {
    fontSize: '10px',
  },
}));

const DevOpsPage: React.FC = () => {
  const theme = useTheme();
  
  const [isVisible, setIsVisible] = useState<VisibilityState>({
    hero: false,
    overview: false,
    features: false,
    process: false,
    technologies: false,
    cta: false,
    testimonials: false
  });
  
  // Initialize the refs with proper typing
  const sectionRefs: SectionRefs = {
    hero: useRef<HTMLElement>(null),
    overview: useRef<HTMLElement>(null),
    features: useRef<HTMLElement>(null),
    process: useRef<HTMLElement>(null),
    technologies: useRef<HTMLElement>(null),
    cta: useRef<HTMLElement>(null),
    testimonials: useRef<HTMLElement>(null)
  };
  
  // Track when sections come into view
  useEffect(() => {
    // Define type for observers
    const observers: Record<string, IntersectionObserver> = {};
    
    Object.entries(sectionRefs).forEach(([section, ref]) => {
      observers[section] = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            setIsVisible(prev => ({ ...prev, [section]: true }));
          }
        },
        { threshold: 0.1 }
      );
      
      if (ref.current) {
        observers[section].observe(ref.current);
      }
    });
    
    return () => {
      Object.entries(observers).forEach(([section, observer]) => {
        if (sectionRefs[section as keyof typeof sectionRefs]?.current) {
          observer.unobserve(sectionRefs[section as keyof typeof sectionRefs].current!);
        }
      });
    };
  }, []);
  
  const handleContactClick = (): void => {
    console.log('Contact us clicked');
    // Add navigation logic here
  };
  
  // Sample portfolio projects
  const portfolioProjects: PortfolioProject[] = [
    {
      id: 1,
      title: 'CI/CD Pipeline Implementation',
      description: 'Automated build, test, and deployment pipeline that reduced deployment time from days to minutes for a fintech company.',
      image: 'https://placehold.co/600x400/e4e4e7/555555?text=CI/CD+Pipeline',
      tags: ['Jenkins', 'Docker', 'Kubernetes', 'AWS']
    },
    {
      id: 2,
      title: 'Cloud Migration & Infrastructure',
      description: 'Successful migration of on-premise applications to cloud infrastructure with zero downtime and 40% cost reduction.',
      image: 'https://placehold.co/600x400/e4e4e7/555555?text=Cloud+Migration',
      tags: ['AWS', 'Terraform', 'Ansible', 'Monitoring']
    },
    {
      id: 3,
      title: 'Microservices Orchestration',
      description: 'Implementation of containerized microservices architecture with automated scaling and robust monitoring systems.',
      image: 'https://placehold.co/600x400/e4e4e7/555555?text=Microservices',
      tags: ['Kubernetes', 'Istio', 'Prometheus', 'ELK Stack']
    }
  ];

  // Feature items
  const featureItems: FeatureItem[] = [
    {
      icon: <CodeIcon fontSize="large" sx={{ color: '#044D76' }} />,
      title: "CI/CD Implementation",
      description: "Build, test, and deploy automation pipelines that enable frequent code integration and rapid, reliable software delivery.",
      delay: 0
    },
    {
      icon: <StorageIcon fontSize="large" sx={{ color: '#044D76' }} />,
      title: "Infrastructure as Code",
      description: "Manage and provision infrastructure through code instead of manual processes, ensuring consistency, scalability, and version control.",
      delay: 100
    },
    {
      icon: <SyncIcon fontSize="large" sx={{ color: '#044D76' }} />,
      title: "Continuous Deployment",
      description: "Automatically deploy your application to production with confidence, using sophisticated testing and rollback mechanisms.",
      delay: 200
    },
    {
      icon: <ViewModuleIcon fontSize="large" sx={{ color: '#044D76' }} />,
      title: "Containerization",
      description: "Implement Docker and container orchestration technologies like Kubernetes to make your applications more portable and scalable.",
      delay: 300
    },
    {
      icon: <AssessmentIcon fontSize="large" sx={{ color: '#044D76' }} />,
      title: "Monitoring & Observability",
      description: "Implement comprehensive monitoring solutions to gain visibility into your applications and infrastructure performance, health, and usage.",
      delay: 400
    },
    {
      icon: <CloudQueueIcon fontSize="large" sx={{ color: '#044D76' }} />,
      title: "Cloud Migration & Management",
      description: "Migrate your applications to the cloud and implement best practices for cloud management, security, and cost optimization.",
      delay: 500
    }
  ];

  // Process steps
  const processSteps: ProcessStep[] = [
    {
      icon: <AutorenewIcon />,
      title: "Assessment & Strategy",
      description: "We evaluate your current workflows, tools, and processes to identify opportunities for improvement and develop a tailored DevOps roadmap.",
      delay: 0
    },
    {
      icon: <BuildIcon />,
      title: "Tool Selection & Integration",
      description: "We help you select and implement the right DevOps tools based on your needs, ensuring they integrate seamlessly with your existing systems.",
      delay: 200
    },
    {
      icon: <ArchitectureIcon />,
      title: "CI/CD Pipeline Setup",
      description: "We build and configure automated pipelines for continuous integration and delivery, enabling frequent code integration and reliable deployments.",
      delay: 400
    },
    {
      icon: <SettingsIcon />,
      title: "Infrastructure Automation",
      description: "We implement Infrastructure as Code practices to automate the provisioning and management of your infrastructure, ensuring consistency and repeatability.",
      delay: 600
    },
    {
      icon: <MonitorIcon />,
      title: "Monitoring & Feedback",
      description: "We set up comprehensive monitoring and observability solutions to gain insights into your systems' performance and health, enabling continuous improvement.",
      delay: 800
    },
    {
      icon: <SettingsApplicationsIcon />,
      title: "Optimization & Scaling",
      description: "We continuously optimize your DevOps processes and infrastructure to improve performance, reduce costs, and scale efficiently as your needs grow.",
      delay: 1000
    }
  ];

  // Technologies
  const technologies: Technology[] = [
    { 
      name: "Jenkins", 
      icon: <img src="https://www.jenkins.io/images/logos/jenkins/jenkins.png" alt="Jenkins" width="48" height="48" />,
      delay: 0 
    },
    { 
      name: "Docker", 
      icon: <img src="https://www.docker.com/wp-content/uploads/2022/03/Moby-logo.png" alt="Docker" width="48" height="48" />,
      delay: 100 
    },
    { 
      name: "Kubernetes", 
      icon: <img src="https://kubernetes.io/images/favicon.png" alt="Kubernetes" width="48" height="48" />,
      delay: 200 
    },
    { 
      name: "Terraform", 
      icon: <img src="https://www.terraform.io/favicon.ico" alt="Terraform" width="48" height="48" />,
      delay: 300 
    },
    { 
      name: "Ansible", 
      icon: <img src="https://www.ansible.com/hubfs/2016_Images/Assets/Ansible-Mark-Large-RGB-Pool.png" alt="Ansible" width="48" height="48" />,
      delay: 400 
    },
    { 
      name: "AWS", 
      icon: <img src="https://a0.awsstatic.com/libra-css/images/site/fav/favicon.ico" alt="AWS" width="48" height="48" />,
      delay: 500 
    },
    { 
      name: "Azure DevOps", 
      icon: <img src="https://cdn.vsassets.io/content/icons/favicon.ico" alt="Azure DevOps" width="48" height="48" />,
      delay: 600 
    },
    { 
      name: "GitLab CI/CD", 
      icon: <img src="https://about.gitlab.com/images/press/logo/png/gitlab-icon-rgb.png" alt="GitLab CI/CD" width="48" height="48" />,
      delay: 700 
    },
    { 
      name: "Prometheus", 
      icon: <img src="https://prometheus.io/assets/favicon.ico" alt="Prometheus" width="48" height="48" />,
      delay: 800 
    },
    { 
      name: "Grafana", 
      icon: <img src="https://grafana.com/static/assets/img/fav32.png" alt="Grafana" width="48" height="48" />,
      delay: 900 
    },
    { 
      name: "ELK Stack", 
      icon: <img src="https://www.elastic.co/favicon.ico" alt="ELK Stack" width="48" height="48" />,
      delay: 1000 
    },
    { 
      name: "GitHub Actions", 
      icon: <img src="https://github.githubassets.com/favicons/favicon.svg" alt="GitHub Actions" width="48" height="48" />,
      delay: 1100 
    }
  ];

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: '#0099ff0a' }}>
      <Navbar />
      <HeroBox component="section" ref={sectionRefs.hero}>
        {/* Background elements */}
        <Box sx={{ position: 'absolute', inset: 0, overflow: 'hidden' }}>
          <GradientCircle 
            sx={{ 
              top: 0, 
              right: 0, 
              width: 800, 
              height: 800, 
              background: `radial-gradient(circle, ${alpha('#0099ff', 0.2)} 0%, transparent 70%)`,
              opacity: 0.2,
              transform: 'translate(25%, -25%)'
            }}
          />
          <GradientCircle 
            sx={{ 
              bottom: 0, 
              left: 0, 
              width: 600, 
              height: 600, 
              background: `radial-gradient(circle, ${alpha('#0099ff', 0.2)} 0%, transparent 70%)`,
              opacity: 0.2,
              transform: 'translate(-33%, 33%)'
            }}
          />
          
          {/* DevOps Pipeline Visual in Background */}
          <DevOpsPipelineBg>
            <Box sx={{ width: '100%', height: '100%', position: 'relative' }}>
              <svg width="100%" height="100%" viewBox="0 0 1200 800" fill="none" xmlns="http://www.w3.org/2000/svg">
                {/* Code/Plan */}
                <rect x="100" y="200" width="150" height="80" rx="10" stroke="white" strokeWidth="2" />
                <text x="175" y="250" textAnchor="middle" fill="white" fontFamily="monospace" fontSize="18">Code</text>
                
                {/* Build */}
                <rect x="350" y="200" width="150" height="80" rx="10" stroke="white" strokeWidth="2" />
                <text x="425" y="250" textAnchor="middle" fill="white" fontFamily="monospace" fontSize="18">Build</text>
                
                {/* Test */}
                <rect x="600" y="200" width="150" height="80" rx="10" stroke="white" strokeWidth="2" />
                <text x="675" y="250" textAnchor="middle" fill="white" fontFamily="monospace" fontSize="18">Test</text>
                
                {/* Deploy */}
                <rect x="850" y="200" width="150" height="80" rx="10" stroke="white" strokeWidth="2" />
                <text x="925" y="250" textAnchor="middle" fill="white" fontFamily="monospace" fontSize="18">Deploy</text>
                
                {/* Monitor */}
                <rect x="600" y="400" width="150" height="80" rx="10" stroke="white" strokeWidth="2" />
                <text x="675" y="450" textAnchor="middle" fill="white" fontFamily="monospace" fontSize="18">Monitor</text>
                
                {/* Feedback */}
                <rect x="350" y="400" width="150" height="80" rx="10" stroke="white" strokeWidth="2" />
                <text x="425" y="450" textAnchor="middle" fill="white" fontFamily="monospace" fontSize="18">Feedback</text>
                
                {/* Plan */}
                <rect x="100" y="400" width="150" height="80" rx="10" stroke="white" strokeWidth="2" />
                <text x="175" y="450" textAnchor="middle" fill="white" fontFamily="monospace" fontSize="18">Plan</text>
                
                {/* Operate */}
                <rect x="850" y="400" width="150" height="80" rx="10" stroke="white" strokeWidth="2" />
                <text x="925" y="450" textAnchor="middle" fill="white" fontFamily="monospace" fontSize="18">Operate</text>
                
                {/* Connecting Arrows - top row */}
                <path d="M250 240 L350 240" stroke="white" strokeWidth="2" />
                <path d="M500 240 L600 240" stroke="white" strokeWidth="2" />
                <path d="M750 240 L850 240" stroke="white" strokeWidth="2" />
                
                {/* Connecting Arrows - right column */}
                <path d="M925 280 L925 400" stroke="white" strokeWidth="2" />
                
                {/* Connecting Arrows - bottom row */}
                <path d="M850 440 L750 440" stroke="white" strokeWidth="2" />
                <path d="M600 440 L500 440" stroke="white" strokeWidth="2" />
                <path d="M350 440 L250 440" stroke="white" strokeWidth="2" />
                
                {/* Connecting Arrow - left column */}
                <path d="M175 400 L175 280" stroke="white" strokeWidth="2" />
                
                {/* Arrow heads */}
                <polygon points="345,240 335,235 335,245" fill="white" />
                <polygon points="595,240 585,235 585,245" fill="white" />
                <polygon points="845,240 835,235 835,245" fill="white" />
                <polygon points="925,395 920,385 930,385" fill="white" />
                <polygon points="755,440 765,435 765,445" fill="white" />
                <polygon points="505,440 515,435 515,445" fill="white" />
                <polygon points="255,440 265,435 265,445" fill="white" />
                <polygon points="175,285 170,295 180,295" fill="white" />
              </svg>
            </Box>
          </DevOpsPipelineBg>
        </Box>

        <Container sx={{ position: 'relative', zIndex: 10 }}>
          <Box maxWidth="md">
            <FadeInBox visible={isVisible.hero}>
              <Typography variant="h2" component="h1" gutterBottom>
                DevOps
                <Box component="span" display="block" sx={{ color: '#0099ff' }}>Services</Box>
              </Typography>
            </FadeInBox>
            
            <FadeInBox visible={isVisible.hero} delay={200}>
              <Typography variant="h5" paragraph sx={{ color: 'grey.300' }}>
                Streamline your development pipeline, automate your infrastructure, and deliver software faster with confidence
              </Typography>
            </FadeInBox>
            
            <FadeInBox visible={isVisible.hero} delay={400} sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <Button 
                variant="contained" 
                size="large" 
                onClick={handleContactClick}
                sx={{ 
                  px: 4, 
                  py: 1.5, 
                  bgcolor: '#044D76',
                  '&:hover': { 
                    bgcolor: alpha('#044D76', 0.9),
                    transform: 'translateY(-4px)'
                  },
                  transition: 'all 0.3s',
                  borderRadius: 2,
                }}
              >
                Get Started
              </Button>
              <Button 
                variant="outlined" 
                color="inherit" 
                size="large" 
                href="#portfolio"
                sx={{ 
                  px: 4, 
                  py: 1.5, 
                  borderColor: 'rgba(255, 255, 255, 0.3)',
                  '&:hover': { 
                    borderColor: 'rgba(255, 255, 255, 0.6)',
                    transform: 'translateY(-4px)'
                  },
                  transition: 'all 0.3s',
                  borderRadius: 2,
                }}
              >
                View Our Work
              </Button>
            </FadeInBox>
          </Box>
        </Container>

        {/* Wave shape divider */}
        <Box sx={{ position: "absolute", bottom: 0, left: 0, width: "100%" }}>
          <svg
            viewBox="0 0 1440 120"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M0 0L48 8.875C96 17.75 192 35.5 288 53.25C384 71 480 88.75 576 80.5C672 71 768 35.5 864 26.625C960 17.75 1056 35.5 1152 44.375C1248 53.25 1344 53.25 1392 53.25H1440V120H1392C1344 120 1248 120 1152 120C1056 120 960 120 864 120C768 120 672 120 576 120C480 120 384 120 288 120C192 120 96 120 48 120H0V0Z"
              fill={theme.palette.background.default}
            />
          </svg>
        </Box>
      </HeroBox>

      {/* Overview Section */}
      <Box component="section" ref={sectionRefs.overview} py={10}>
        <Container maxWidth="lg">
          <Grid container spacing={6} alignItems="center">
            <Grid item xs={12} md={6}>
              <FadeInBox visible={isVisible.overview}>
                <Typography variant="h3" component="h2" gutterBottom sx={{ color: '#044D76' }}>
                  Accelerate Your Software Delivery
                </Typography>
                <Typography variant="body1" paragraph>
                  DevOps is more than just tools and automation—it's a culture that brings development and operations teams together to build, test, and deploy applications faster and more reliably. Our DevOps services help you implement this culture and the tools that support it.
                </Typography>
                <Typography variant="body1" paragraph>
                  We help organizations of all sizes implement DevOps practices that reduce time-to-market, improve system reliability, and increase the efficiency of your entire development lifecycle.
                </Typography>
                
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 4 }}>
                  {[
                    "Continuous Integration & Delivery",
                    "Infrastructure as Code",
                    "Containerization & Orchestration",
                    "Monitoring & Observability"
                  ].map((item, index) => (
                    <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <CheckCircleIcon sx={{ color: '#044D76' }} />
                      <Typography variant="body1" fontWeight="medium">{item}</Typography>
                    </Box>
                  ))}
                </Box>
              </FadeInBox>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <FadeInBox visible={isVisible.overview} delay={300}>
                <Box sx={{ position: 'relative' }}>
                  <CodeEditor>
                    <EditorHeader>
                      <EditorDot color="#ff5f56" />
                      <EditorDot color="#ffbd2e" />
                      <EditorDot color="#27c93f" />
                      <EditorFilename>terraform.tf</EditorFilename>
                    </EditorHeader>
                    
                    <CodeContent>
                      <PreformattedCode>
{`# Infrastructure as Code example with Terraform

provider "aws" {
  region = var.aws_region
}

# VPC Configuration
resource "aws_vpc" "main" {
  cidr_block           = var.vpc_cidr
  enable_dns_support   = true
  enable_dns_hostnames = true

  tags = {
    Name        = "\${var.project_name}-vpc"
    Environment = var.environment
  }
}

# Subnets
resource "aws_subnet" "public" {
  count                   = length(var.public_subnets)
  vpc_id                  = aws_vpc.main.id
  cidr_block              = var.public_subnets[count.index]
  availability_zone       = var.availability_zones[count.index]
  map_public_ip_on_launch = true

  tags = {
    Name        = "\${var.project_name}-public-subnet-\${count.index}"
    Environment = var.environment
  }
}

resource "aws_subnet" "private" {
  count                   = length(var.private_subnets)
  vpc_id                  = aws_vpc.main.id
  cidr_block              = var.private_subnets[count.index]
  availability_zone       = var.availability_zones[count.index]
  map_public_ip_on_launch = false

  tags = {
    Name        = "\${var.project_name}-private-subnet-\${count.index}"
    Environment = var.environment
  }
}

# Security Group
resource "aws_security_group" "app_sg" {
  name        = "\${var.project_name}-sg"
  description = "Security group for application servers"
  vpc_id      = aws_vpc.main.id

  ingress {
    from_port   = 80
    to_port     = 80
    protocol    = "tcp"
    cidr_blocks = ["0.0.0.0/0"]
  }

  egress {
    from_port   = 0
    to_port     = 0
    protocol    = "-1"
    cidr_blocks = ["0.0.0.0/0"]
  }

  tags = {
    Name        = "\${var.project_name}-sg"
    Environment = var.environment
  }
}`}
                      </PreformattedCode>
                    </CodeContent>
                  </CodeEditor>
                  
                  {/* Background decoration */}
                  <Box 
                    sx={{ 
                      position: 'absolute', 
                      top: -24, 
                      right: -24, 
                      width: 128, 
                      height: 128, 
                      borderRadius: '50%', 
                      bgcolor: '#0099ff',
                      opacity: 0.2,
                      zIndex: -1
                    }}
                  />
                  <Box 
                    sx={{ 
                      position: 'absolute', 
                      bottom: -32, 
                      left: -32, 
                      width: 160, 
                      height: 160, 
                      borderRadius: '50%', 
                      bgcolor: '#044D76',
                      opacity: 0.2,
                      zIndex: -1
                    }}
                  />
                </Box>
              </FadeInBox>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Features Section */}
      <Box 
        component="section" 
        ref={sectionRefs.features}
        py={10}
        sx={{ bgcolor: '#ffffff' }}
      >
        <Container maxWidth="lg">
          <FadeInBox 
            visible={isVisible.features}
            sx={{ textAlign: 'center', maxWidth: 'md', mx: 'auto', mb: 8 }}
          >
            <Typography variant="h3" component="h2" gutterBottom sx={{ color: '#044D76' }}>
              Our DevOps Services
            </Typography>
            <Typography variant="body1" color="text.secondary">
              We offer comprehensive DevOps solutions to help you streamline your development process, automate your infrastructure, and deliver software faster and more reliably.
            </Typography>
          </FadeInBox>
          
          <Grid container spacing={4}>
            {featureItems.map((feature, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <FeatureCard 
                  elevation={2}
                  visible={isVisible.features}
                  delay={feature.delay}
                >
                  <IconBox className="featureIcon">
                    {feature.icon}
                  </IconBox>
                  <Typography 
                    variant="h6" 
                    component="h3" 
                    gutterBottom
                    className="featureTitle"
                  >
                    {feature.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {feature.description}
                  </Typography>
                </FeatureCard>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Implementation Process Section */}
      <Box component="section" ref={sectionRefs.process} py={10} sx={{ backgroundColor: '#0099ff0a' }}>
        <Container maxWidth="lg">
          <FadeInBox 
            visible={isVisible.process}
            sx={{ textAlign: 'center', maxWidth: 'md', mx: 'auto', mb: 8 }}
          >
            <Typography variant="h3" component="h2" gutterBottom sx={{ color: '#044D76' }}>
              Our DevOps Implementation Process
            </Typography>
            <Typography variant="body1" color="text.secondary">
              We follow a structured approach to implementing DevOps practices in your organization, ensuring a smooth transition and measurable results.
            </Typography>
          </FadeInBox>
          
          <Box sx={{ position: 'relative' }}>
            {/* Timeline line */}
            <ProcessTimelineLine />
            
            {/* Process steps */}
            {processSteps.map((step, index) => (
              <ProcessStepBox 
                key={index}
                visible={isVisible.process}
                delay={step.delay}
                sx={{ 
                  flexDirection: index % 2 === 0 ? 'row' : 'row-reverse',
                }}
              >
                <Box 
                  sx={{ 
                    width: '50%',
                    textAlign: index % 2 === 0 ? 'right' : 'left',
                    pr: index % 2 === 0 ? 6 : 0,
                    pl: index % 2 === 0 ? 0 : 6,
                  }}
                >
                  <Typography variant="h6" component="h3" gutterBottom sx={{ color: '#044D76' }}>
                    {step.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {step.description}
                  </Typography>
                </Box>
                
                <ProcessIconBox>
                  {step.icon}
                </ProcessIconBox>
                
                <Box sx={{ width: '50%' }} />
              </ProcessStepBox>
            ))}
          </Box>
        </Container>
      </Box>

      {/* Technologies Section */}
      <Box 
        component="section" 
        ref={sectionRefs.technologies}
        py={10}
        sx={{ bgcolor: '#ffffff' }}
      >
        <Container maxWidth="lg">
          <FadeInBox 
            visible={isVisible.technologies}
            sx={{ textAlign: 'center', maxWidth: 'md', mx: 'auto', mb: 8 }}
          >
            <Typography variant="h3" component="h2" gutterBottom sx={{ color: '#044D76' }}>
              DevOps Technologies We Use
            </Typography>
            <Typography variant="body1" color="text.secondary">
              We leverage industry-leading DevOps tools and technologies to build and optimize your development and deployment pipelines.
            </Typography>
          </FadeInBox>
          
          <Grid container spacing={4}>
            {technologies.map((tech, index) => (
              <Grid item xs={6} sm={4} md={3} key={index}>
                <TechCard 
                  elevation={1}
                  visible={isVisible.technologies}
                  delay={tech.delay}
                >
                  <TechIconBox className="techIcon">
                    {tech.icon}
                  </TechIconBox>
                  <Typography variant="h6" fontWeight="medium" sx={{ color: '#044D76', mb: 1 }}>
                    {tech.name}
                  </Typography>
                  <Divider sx={{ width: '40%', mx: 'auto', my: 2, backgroundColor: 'rgba(4, 77, 118, 0.1)' }} />
                  <Typography variant="body2" color="text.secondary">
                    Expert implementation
                  </Typography>
                </TechCard>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Portfolio Section */}
      <Box component="section" id="portfolio" py={10} sx={{ backgroundColor: '#0099ff0a' }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', maxWidth: 'md', mx: 'auto', mb: 8 }}>
            <Typography variant="h3" component="h2" gutterBottom sx={{ color: '#044D76' }}>
              Our DevOps Success Stories
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Explore some of our successful DevOps implementations and the results we've delivered for our clients.
            </Typography>
          </Box>
          
          <Grid container spacing={4}>
            {portfolioProjects.map((project) => (
              <Grid item xs={12} sm={6} md={4} key={project.id}>
                <ProjectCard elevation={4}>
                  <Box sx={{ height: 240, position: 'relative' }}>
                    <ProjectImage 
                      src={project.image} 
                      alt={project.title}
                      className="projectImage"
                    />
                    <ProjectOverlay className="projectOverlay">
                      <Typography variant="h6" component="h3" gutterBottom sx={{ color: 'common.white' }}>
                        {project.title}
                      </Typography>
                      <Typography variant="body2" paragraph sx={{ color: 'grey.200' }}>
                        {project.description}
                      </Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                        {project.tags.map((tag, i) => (
                          <Chip 
                            key={i}
                            label={tag}
                            size="small"
                            sx={{ 
                              bgcolor: 'rgba(4, 77, 118, 0.8)', 
                              color: 'white',
                              fontWeight: 'medium',
                              fontSize: '0.75rem'
                            }}
                          />
                        ))}
                      </Box>
                    </ProjectOverlay>
                  </Box>
                </ProjectCard>
              </Grid>
            ))}
          </Grid>
          
          <Box sx={{ textAlign: 'center', mt: 6 }}>
            <Button 
              variant="contained" 
              size="large"
              sx={{ 
                px: 4, 
                py: 1.5, 
                bgcolor: '#044D76',
                color: 'common.white',
                '&:hover': {
                  bgcolor: alpha('#044D76', 0.9),
                  transform: 'translateY(-4px)'
                },
                transition: 'all 0.3s ease',
                borderRadius: 2,
              }}
            >
              View All Projects
            </Button>
          </Box>
        </Container>
      </Box>

      {/* Testimonials Section */}
      <Box component="section" ref={sectionRefs.testimonials} py={10} sx={{ backgroundColor: '#0099ff0a' }}>
        <Container maxWidth="lg">
          <FadeInBox 
            visible={isVisible.testimonials}
            sx={{ textAlign: 'center', maxWidth: 'md', mx: 'auto', mb: 8 }}
          >
            <Typography variant="h3" component="h2" gutterBottom sx={{ color: '#044D76' }}>
              Customer Testimonials
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Discover what our clients have to say about their experience working with us and the results we've delivered.
            </Typography>
          </FadeInBox>
          
          <Box sx={{ position: 'relative', maxWidth: 'md', mx: 'auto' }}>
            {/* Left Navigation */}
            <TestimonialNavButton 
              sx={{ 
                left: { xs: 10, md: -70 },
                top: '50%', 
                transform: 'translateY(-50%)',
              }}
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M15 18L9 12L15 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </TestimonialNavButton>
            
            {/* Testimonial Card */}
            <TestimonialCard elevation={3}>
              <Typography 
                variant="body1" 
                component="blockquote" 
                sx={{ 
                  fontStyle: 'italic', 
                  position: 'relative',
                  mb: 4,
                  '&::before': {
                    content: '"\\201C"',
                    position: 'absolute',
                    left: -30,
                    top: -20,
                    fontSize: '5rem',
                    color: 'rgba(4, 77, 118, 0.1)',
                    fontFamily: 'Georgia, serif'
                  }
                }}
              >
                "The DevOps implementation has transformed our development process. Deployment time went from days to minutes, and our team collaboration has never been better. The ROI on this project was achieved in just 4 months due to increased productivity and reduced infrastructure costs."
              </Typography>
              
              <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                {[...Array(5)].map((_, index) => (
                  <Box 
                    key={index}
                    component="span"
                    sx={{ 
                      color: '#0099ff',
                      mx: 0.5,
                      fontSize: '24px',
                    }}
                  >
                    ★
                  </Box>
                ))}
              </Box>
              
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Box 
                  component="img"
                  src="https://randomuser.me/api/portraits/men/42.jpg"
                  alt="Michael Thompson"
                  sx={{
                    width: 60,
                    height: 60,
                    borderRadius: '50%',
                    mr: 2,
                    border: '2px solid rgba(4, 77, 118, 0.2)'
                  }}
                />
                <Box>
                  <Typography variant="h6" sx={{ color: '#044D76', fontWeight: 'bold' }}>
                    Michael Thompson
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    CTO, TechSolutions Inc.
                  </Typography>
                </Box>
              </Box>
            </TestimonialCard>
            
            {/* Right Navigation */}
            <TestimonialNavButton 
              sx={{ 
                right: { xs: 10, md: -70 },
                top: '50%', 
                transform: 'translateY(-50%)',
              }}
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M9 6L15 12L9 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </TestimonialNavButton>
          </Box>
        </Container>
      </Box>
      <LetsTalk/>
      <Footer />
    </Box>
  );
};

export default DevOpsPage;